import React, { useMemo, useState } from 'react'
import Link from 'next/link'
import SinglePack from '../../SinglePack'
import * as gtag from '../../../lib/gtag'
import styles from './PublicPackageAdvisor.module.scss'

const GOALS = [
  ['corporate', 'Corporate meeting or presentation'],
  ['celebration', 'Private celebration'],
  ['concert', 'Live music or concert'],
  ['wedding', 'Wedding'],
  ['trade', 'Trade show or brand activation'],
  ['visual', 'Projection, screens, or video'],
]

const normalizeText = value => String(value || '').toLowerCase()

const matchesGoal = (pack, goal) => {
  const text = normalizeText([
    pack.name,
    pack.package_type,
    pack.public_profile?.family,
    pack.public_profile?.outcome,
    ...(pack.tags || []),
    ...(pack.public_profile?.bestFor || []),
  ].join(' '))
  if (goal === 'visual') return /visual|projector|video|screen|led/.test(text)
  if (goal === 'celebration') return /celebration|party|private/.test(text)
  if (goal === 'trade') return /trade|brand|exhibit|activation/.test(text)
  return text.includes(goal)
}

const scoreSetup = (pack, answers) => {
  const maxGuests = Number(pack.public_profile?.audience?.maxGuests || pack.guests || 0)
  const guests = Number(answers.guests || 0)
  const price = Number(pack.price || 0)
  const budget = Number(answers.budget || 0)
  const venue = pack.public_profile?.audience?.venue || 'either'
  const salesPath = pack.public_profile?.salesPath || 'both'
  let score = matchesGoal(pack, answers.goal) ? 100 : -50
  if (maxGuests) {
    if (maxGuests >= guests) score += Math.max(5, 25 - Math.abs(maxGuests - guests) / Math.max(guests, 25) * 8)
    else score -= 35
  }
  if (answers.venue === 'either' || venue === 'either' || venue === answers.venue) score += 8
  else score -= 18
  if (answers.service === 'managed') score += ['expert_review', 'both'].includes(salesPath) ? 10 : -12
  if (answers.service === 'self_service') score += ['self_service', 'both'].includes(salesPath) ? 10 : -12
  if (budget) score += price <= budget ? 12 : -Math.min(25, (price - budget) / Math.max(budget, 1) * 15)
  score -= Number(pack.public_profile?.rank || 100) / 100
  return score
}

const recommendSetups = (packages, answers) => {
  const intentMatches = packages.filter(pack => matchesGoal(pack, answers.goal))
  const guests = Number(answers.guests || 0)
  const capacityMatches = intentMatches.filter(pack => {
    const maxGuests = Number(pack.public_profile?.audience?.maxGuests || pack.guests || 0)
    return !guests || !maxGuests || maxGuests >= guests
  })
  const ranked = [...capacityMatches]
    .map(pack => ({ pack, score: scoreSetup(pack, answers) }))
    .sort((a, b) => b.score - a.score)
  if (!ranked.length) return []
  const best = ranked[0]
  const lower = ranked
    .filter(item => item.pack._id !== best.pack._id && Number(item.pack.price) < Number(best.pack.price))
    .sort((a, b) => b.score - a.score)[0]
  const moreCapable = ranked
    .filter(item => item.pack._id !== best.pack._id && item.pack._id !== lower?.pack?._id)
    .sort((a, b) => (
      Number(b.pack.public_profile?.audience?.maxGuests || b.pack.guests || 0)
      - Number(a.pack.public_profile?.audience?.maxGuests || a.pack.guests || 0)
    ) || b.score - a.score)[0]
  const fallbacks = ranked.filter(item => ![best.pack._id, lower?.pack?._id, moreCapable?.pack?._id].includes(item.pack._id))
  return [
    { ...best, label: 'Best fit', description: 'The closest match for your event, audience, and service preference.' },
    lower ? { ...lower, label: 'Lower equipment price', description: 'A simpler starting point that stays closer to a lower budget.' } : null,
    moreCapable ? { ...moreCapable, label: 'More capable', description: 'More capacity or flexibility if the event grows.' } : null,
    ...fallbacks.map(item => ({ ...item, label: 'Alternative', description: 'Another setup worth comparing.' })),
  ].filter(Boolean).slice(0, 3)
}

const PublicPackageAdvisor = ({ packages = [] }) => {
  const [answers, setAnswers] = useState({ goal: 'corporate', guests: 50, venue: 'either', service: 'self_service', budget: '' })
  const [submitted, setSubmitted] = useState(false)
  const recommendations = useMemo(() => recommendSetups(packages, answers), [answers, packages])
  const displayedRecommendations = submitted
    ? recommendations
    : packages.slice(0, 3).map((pack, index) => ({
      pack,
      label: index === 0 ? 'Popular starting point' : 'Compare another approach',
      description: pack.public_profile?.outcome || 'A reviewed setup worth comparing.',
    }))

  const update = event => setAnswers(current => ({ ...current, [event.target.name]: event.target.value }))
  const submit = event => {
    event.preventDefault()
    setSubmitted(true)
    gtag.event({
      action: 'package_advisor_complete',
      category: 'orders',
      label: 'leads',
      value: 4,
      filterState: JSON.stringify(answers),
      resultCount: recommendations.length,
      sourceSurface: 'recommended_setup_advisor'
    })
    window.requestAnimationFrame(() => document.getElementById('recommended-setups-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  }

  return <section className={styles.advisor} id='setup-advisor' aria-labelledby='setup-advisor-title'>
    <div className={styles.intro}>
      <span>Recommended AV starting setups</span>
      <h2 id='setup-advisor-title'>Start with a proven setup. Change anything.</h2>
      <p>These are editable equipment starting points—not locked bundles. Compare a few sensible options, then add or remove equipment in the Event Builder before committing.</p>
    </div>

    <form className={styles.form} onSubmit={submit}>
      <label>
        <span>What are you planning?</span>
        <select name='goal' value={answers.goal} onChange={update}>
          {GOALS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </label>
      <label>
        <span>Expected guests</span>
        <input name='guests' type='number' inputMode='numeric' min='10' max='10000' step='10' value={answers.guests} onChange={update} />
      </label>
      <label>
        <span>Venue</span>
        <select name='venue' value={answers.venue} onChange={update}>
          <option value='either'>Not sure yet</option>
          <option value='indoor'>Indoor</option>
          <option value='outdoor'>Outdoor</option>
        </select>
      </label>
      <label>
        <span>How do you want to work?</span>
        <select name='service' value={answers.service} onChange={update}>
          <option value='self_service'>Build and price online</option>
          <option value='managed'>Have an AV expert plan it</option>
        </select>
      </label>
      <label>
        <span>Equipment budget <small>(optional)</small></span>
        <input name='budget' type='number' inputMode='decimal' min='0' step='100' placeholder='Example: 2500' value={answers.budget} onChange={update} />
      </label>
      <button type='submit'>Show my best starting points</button>
    </form>

    <div className={styles.expertPath}>
      <div>
        <strong>Need a managed production instead?</strong>
        <span>An AV expert can design around your venue, schedule, crew, power, and show requirements.</span>
      </div>
      <a href='tel:+13059744411'>Call 305 974 4411</a>
      <Link href='/contact-us'>Request expert help</Link>
    </div>

    <div className={styles.results} id='recommended-setups-results' aria-live='polite'>
      <div className={styles.resultsHead}>
        <div>
          <span>{submitted ? 'Your comparison' : 'Popular starting points'}</span>
          <h2>{displayedRecommendations.length ? `${displayedRecommendations.length} setups to compare` : 'Let an expert design this one'}</h2>
        </div>
        {submitted ? <button type='button' onClick={() => setSubmitted(false)}>Adjust answers</button> : null}
      </div>
      {displayedRecommendations.length ? <div className={styles.grid}>
        {displayedRecommendations.map(({ pack, label, description }) => <SinglePack
          key={pack._id}
          pack={pack}
          variant='system-solution'
          contextLabel={label}
          contextDescription={description}
          conversionKey={`package-advisor:${normalizeText(label).replace(/\s+/g, '-')}`}
        />)}
      </div> : <div className={styles.empty}>
        <p>None of the published self-service starting setups safely match these requirements. We will not force a misleading package recommendation.</p>
        <Link href='/contact-us'>Ask an AV expert to design it</Link>
      </div>}
    </div>
  </section>
}

export default PublicPackageAdvisor
